import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Loader2, ShieldAlert } from 'lucide-react';
import {
  REMEDIATION_LIMIT_KEYS, REMEDIATION_LIMIT_LABELS, REMEDIATION_STEP_LABELS, REMEDIATION_SWITCHES, REMEDIATION_SWITCH_LABELS,
  type RemediationMode, type RemediationStep,
} from '@evi/shared';
import { getDisplayErrorMessage } from '@/lib/api';
import { actionLine, fetchLadder, fetchLadderActions, saveLadder, type LadderSettings } from '@/lib/remediation';
import styles from './SettingsPage.module.css';

/**
 * Automatic fixes, the remediation ladder's kill switch (Warren, 28 Oct 2026, evening addendum item 1: "with a kill
 * switch in Admin and limits per charger"; "b and c run 3 days in shadow mode (log would have done), then go live
 * automatically unless I say stop"). Super Admin only (Api: routes/settings/remediation.ts); every save is audited.
 *
 *   the master switch    off until Warren turns it on (it ships off, 29 Oct; turning it on starts b and c's shadow
 *                        clock); off stops every step at once; the restore pass still puts back a connector the ladder took out
 *   a switch per step    a to e
 *   keep b and c in shadow  Warren's stop: they keep logging what they would have done past the 3 days
 *   the limits           only lowered, never above Warren's
 *   the log              the last 50 actions, live and shadow, with what each did and whether the problem cleared
 */
const KEY = ['remediation-ladder'];
const when = (iso: string | null) => (iso ? new Date(iso).toLocaleString() : 'not yet');

export function RemediationSettingsSection() {
  const client = useQueryClient();
  const q = useQuery({ queryKey: KEY, queryFn: fetchLadder, retry: false, staleTime: 30_000 });
  const [mode, setMode] = useState<RemediationMode | ''>('');
  const log = useQuery({ queryKey: [...KEY, 'actions', mode], queryFn: () => fetchLadderActions({ mode }), retry: false, staleTime: 30_000 });
  const [draft, setDraft] = useState<LadderSettings | null>(null), [saving, setSaving] = useState(false), [error, setError] = useState(''), [saved, setSaved] = useState(false);
  if (q.isError) return null;
  const value = draft ?? q.data?.value ?? null;
  const dirty = !!draft && JSON.stringify(draft) !== JSON.stringify(q.data?.value);
  const edit = (next: LadderSettings) => { setDraft(next); setSaved(false); };
  async function save() {
    if (!draft) return;
    setSaving(true); setError(''); setSaved(false);
    try { await saveLadder(draft); await client.invalidateQueries({ queryKey: KEY }); setDraft(null); setSaved(true); }
    catch (err) { setError(await getDisplayErrorMessage(err, 'The setting was not saved.')); } finally { setSaving(false); }
  }
  const shadow = q.data?.shadow;
  return <section className={styles.platformFeeSection}>
    <div className={styles.platformFeeHeader}>
      <div className={styles.headerIcon}><ShieldAlert size={16} /></div>
      <div>
        <h2 className={styles.platformFeeTitle}>Automatic fixes</h2>
        <p className={styles.platformFeeHint}>When a charger is stuck the platform fixes it itself, then asks the building to cycle the breaker, then opens a
          service call. Every action is logged with what triggered it, the command, the reply and whether it cleared. Automatic fixes are off until you
          turn them on here: until then nothing is sent to a charger, no building is asked and no service call is opened by them. Turning them on starts
          the 3 days b and c spend in shadow. Switching them off again stops every step at once; a connector the fixes took out of service is still put
          back.</p>
      </div>
    </div>
    {q.isLoading || !value ? <p className={styles.platformFeeHint}><Loader2 size={13} className={styles.spin} /> Loading.</p> : <>
      <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontWeight: 600, margin: '8px 0' }}>
        <input type="checkbox" checked={value.enabled} onChange={(e) => edit({ ...value, enabled: e.target.checked })} disabled={saving} />
        {value.enabled ? 'Automatic fixes are on' : 'Automatic fixes are off until you turn them on'}
      </label>
      <fieldset style={{ border: 'none', margin: '0 0 12px', padding: 0, display: 'flex', flexDirection: 'column', gap: 6 }} disabled={saving || !value.enabled}>
        {REMEDIATION_SWITCHES.map((s) => <label key={s} style={{ display: 'flex', gap: 8, alignItems: 'center', minHeight: 28 }}>
          <input type="checkbox" checked={value.steps[s]} onChange={(e) => edit({ ...value, steps: { ...value.steps, [s]: e.target.checked } })} />
          {REMEDIATION_SWITCH_LABELS[s]}
        </label>)}
      </fieldset>
      <p className={styles.platformFeeHint} role="status">
        {!shadow?.started_at
          ? <>b and c spend 3 days in shadow (they log what they would have done) from when you first turn automatic fixes on, then go live unless you keep them in shadow.</>
          : shadow.b === 'live' && shadow.c === 'live'
          ? <>b and c are live since {when(shadow.live_at)}.</>
          : value.keep_in_shadow
            ? <>b and c stay in shadow: they log what they would have done and send nothing.</>
            : <>b and c are in shadow (they log what they would have done) until {when(shadow?.live_at ?? null)}, then go live unless you keep them in shadow.</>}
      </p>
      <label style={{ display: 'flex', gap: 8, alignItems: 'center', margin: '4px 0 12px' }}>
        <input type="checkbox" checked={value.keep_in_shadow} onChange={(e) => edit({ ...value, keep_in_shadow: e.target.checked })} disabled={saving} />
        Keep b and c in shadow
      </label>
      <div className={styles.platformFeeField}>
        {REMEDIATION_LIMIT_KEYS.map((k) => <div key={k} className={styles.platformFeeInputRow} style={{ alignItems: 'center', marginBottom: 6 }}>
          <label className={styles.platformFeeLabel} htmlFor={`ladder-${k}`} style={{ flex: '1 1 260px', margin: 0 }}>{REMEDIATION_LIMIT_LABELS[k]} (at most {q.data!.max_limits[k]})</label>
          <input id={`ladder-${k}`} className={styles.platformFeeInput} style={{ maxWidth: 90 }} type="number" min={0} max={q.data!.max_limits[k]} step={1} value={value.limits[k]}
            onChange={(e) => edit({ ...value, limits: { ...value.limits, [k]: Math.max(0, Math.min(q.data!.max_limits[k], Number.parseInt(e.target.value || '0', 10) || 0)) } })}
            disabled={saving} />
        </div>)}
      </div>
      <button type="button" className={styles.platformFeeBtn} disabled={!dirty || saving} onClick={() => void save()}>
        {saving ? <><Loader2 size={13} className={styles.spin} /> Saving</> : saved && !dirty ? <><Check size={13} /> Saved</> : <>Save</>}
      </button>
      {error && <p className={styles.platformFeeHint} role="alert" style={{ color: 'var(--color-err)' }}>{error}</p>}

      <h3 className={styles.platformFeeTitle} style={{ marginTop: 20, fontSize: 15 }}>Recent automatic actions</h3>
      <label className={styles.platformFeeLabel} htmlFor="ladder-log-mode">Show</label>
      <select id="ladder-log-mode" className={styles.platformFeeInput} style={{ maxWidth: 220 }} value={mode} onChange={(e) => setMode(e.target.value as RemediationMode | '')}>
        <option value="">Live and shadow</option>
        <option value="shadow">Shadow (would have done)</option>
        <option value="live">Live</option>
      </select>
      {log.isLoading ? <p className={styles.platformFeeHint}><Loader2 size={13} className={styles.spin} /> Loading.</p>
        : !log.data?.length ? <p className={styles.platformFeeHint}>No automatic actions yet.</p>
          : <div style={{ overflowX: 'auto', marginTop: 8 }}><table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead><tr>{['When', 'Charger', 'Site', 'Step', 'Why', 'Result', 'Cleared'].map((h) => <th key={h} style={{ textAlign: 'left', padding: '4px 8px' }}>{h}</th>)}</tr></thead>
            <tbody>{log.data.map((a) => <tr key={a.id} style={{ borderTop: '1px solid var(--color-border, #dce3df)' }}>
              <td style={{ padding: '4px 8px', whiteSpace: 'nowrap' }}>{new Date(a.at).toLocaleString()}</td>
              <td style={{ padding: '4px 8px' }}><a href={`/chargers/${a.charger_id}`}>{a.identity || a.charger_id}</a></td>
              <td style={{ padding: '4px 8px' }}>{a.location ?? ''}</td>
              <td style={{ padding: '4px 8px' }}>{a.step}</td>
              <td style={{ padding: '4px 8px' }}>{a.trigger.replace(/_/g, ' ')}</td>
              <td style={{ padding: '4px 8px' }}>{actionLine(a, REMEDIATION_STEP_LABELS as Record<RemediationStep, string>)}</td>
              <td style={{ padding: '4px 8px' }}>{a.cleared === null ? '' : a.cleared ? (a.mode === 'shadow' ? 'Cleared on its own' : 'Yes') : 'No'}</td>
            </tr>)}</tbody>
          </table></div>}
    </>}
  </section>;
}
